import type { TripProgress } from '@rfleet/shared';
import clsx from 'clsx';
import { Clock, Flag, MapPin, Navigation, Route } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Badge } from '../../components/ui';
import { fmtDateTime, fmtDuration, fmtNumber } from '../../lib/format';

const fmtTime = (s: string | null, lang: string) =>
  s ? new Intl.DateTimeFormat(lang === 'en' ? 'en-EG' : 'ar-EG-u-nu-latn', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Cairo' }).format(new Date(s)) : '—';

/** Same calendar day in Cairo → show the time only, else date + time. */
const fmtWhen = (s: string | null, lang: string) => {
  if (!s) return '—';
  const day = (d: Date) => new Intl.DateTimeFormat('en-CA', { timeZone: 'Africa/Cairo', dateStyle: 'short' }).format(d);
  return day(new Date(s)) === day(new Date()) ? fmtTime(s, lang) : fmtDateTime(s, lang);
};

/**
 * "Where is this trip going and when will it get there" — shown in the live map panel for the
 * selected vehicle. `kind` distinguishes the running trip from the next scheduled one.
 */
export function TripProgressCard({ p, kind }: { p: TripProgress; kind: 'active' | 'upcoming' }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const origin = p.waypoints.find((w) => w.kind === 'origin') ?? null;
  const dest = p.waypoints.find((w) => w.kind === 'dest') ?? null;
  const stops = p.waypoints.filter((w) => w.kind === 'stop');
  const stopsDone = stops.filter((w) => w.done).length;
  const running = p.status === 'IN_PROGRESS';
  const delay = p.delayMin;
  const delayTone = delay === null ? null : delay > 5 ? 'danger' : delay < -5 ? 'info' : 'success';

  return (
    <div className="flex flex-col gap-2 rounded-lg border border-border bg-surface-2 p-2.5 text-xs">
      {/* header */}
      <div className="flex items-center gap-2">
        <Route size={14} className="flex-none text-info-fg" />
        <Link to={`/trips/${p.tripId}`} className="font-bold hover:underline" dir="ltr">{p.number}</Link>
        <Badge tone={kind === 'active' ? (running ? 'success' : 'warning') : 'info'} dot>
          {kind === 'active' ? (running ? t('live.openTrip') : t('live.pausedTrip')) : t('live.upcomingTrip')}
        </Badge>
        {delayTone && running ? (
          <Badge tone={delayTone}>{delay! > 5 ? t('live.delayed', { min: delay }) : delay! < -5 ? t('live.early', { min: -delay! }) : t('live.onTime')}</Badge>
        ) : null}
        <Link to={`/trips/${p.tripId}`} className="ms-auto text-[11px] text-text-muted hover:underline">{t('live.tripDetails')}</Link>
      </div>

      {/* from → to with times */}
      <div className="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1">
        <MapPin size={13} className="mt-0.5 text-brand-ink" />
        <div className="min-w-0">
          <div className="truncate"><span className="text-text-muted">{t('live.tripFrom')}:</span> <b>{origin?.label ?? '—'}</b></div>
          <div className="text-[11px] text-text-muted">
            {p.startedAt ? `${t('live.startedAt')} ${fmtWhen(p.startedAt, lang)}` : p.scheduledAt ? `${t('live.scheduledAt')} ${fmtWhen(p.scheduledAt, lang)}` : ''}
          </div>
        </div>
        <Flag size={13} className="mt-0.5 text-[#DB2777]" />
        <div className="min-w-0">
          <div className="truncate"><span className="text-text-muted">{t('live.tripTo')}:</span> <b>{dest?.label ?? '—'}</b></div>
          <div className="text-[11px] text-text-muted">
            {p.dueAt ? `${t('live.dueAt')} ${fmtWhen(p.dueAt, lang)}` : ''}
            {stops.length ? ` · ${t('live.stopsDone', { done: stopsDone, total: stops.length })}` : ''}
          </div>
        </div>
      </div>

      {/* progress bar */}
      {kind === 'active' && p.progressPct !== null ? (
        <div className="h-1.5 w-full overflow-hidden rounded-full bg-border" role="progressbar" aria-valuenow={p.progressPct} aria-valuemin={0} aria-valuemax={100}>
          <div className={clsx('h-full rounded-full', running ? 'bg-success' : 'bg-warning')} style={{ width: `${p.progressPct}%` }} />
        </div>
      ) : null}

      {/* KPIs */}
      <dl className="grid grid-cols-3 gap-1.5 text-center">
        {kind === 'active' ? (
          <div className="rounded-md bg-surface p-1.5">
            <dt className="text-[10px] text-text-muted">{t('live.driven')}</dt>
            <dd className="text-sm font-bold">{fmtNumber(p.drivenKm, lang, 1)} <span className="text-[10px] font-normal">{t('units.km')}</span></dd>
            <dd className="text-[10px] text-text-muted">{p.elapsedSec !== null ? `${t('live.elapsed')} ${fmtDuration(p.elapsedSec, lang)}` : ''}</dd>
          </div>
        ) : (
          <div className="rounded-md bg-surface p-1.5">
            <dt className="text-[10px] text-text-muted">{t('trips.distance')}</dt>
            <dd className="text-sm font-bold">{fmtNumber(p.plannedKm, lang, 0)} <span className="text-[10px] font-normal">{t('units.km')}</span></dd>
          </div>
        )}
        <div className="rounded-md bg-surface p-1.5">
          <dt className="text-[10px] text-text-muted">{t('live.remaining')}</dt>
          <dd className="text-sm font-bold">{fmtNumber(p.remainingKm, lang, p.remainingKm !== null && p.remainingKm < 10 ? 1 : 0)} <span className="text-[10px] font-normal">{t('units.km')}</span></dd>
          <dd className="text-[10px] text-text-muted">{p.remainingSec !== null ? `≈ ${fmtDuration(Math.max(60, p.remainingSec), lang)}` : ''}</dd>
        </div>
        <div className="rounded-md bg-surface p-1.5">
          <dt className="text-[10px] text-text-muted">{t('live.eta')}</dt>
          <dd className={clsx('text-sm font-bold', delayTone === 'danger' && running && 'text-danger-fg')}>{fmtWhen(p.etaAt, lang)}</dd>
          <dd className="text-[10px] text-text-muted">{p.etaSpeedKph !== null ? `${p.etaSpeedKph} ${t('units.kph')}` : ''}</dd>
        </div>
      </dl>

      {/* next stop */}
      {p.nextWaypoint ? (
        <div className="flex items-center gap-1.5 text-[11px]">
          <Navigation size={12} className="flex-none text-info-fg" />
          <span className="text-text-muted">{p.nextWaypoint.kind === 'dest' ? t('live.destination') : `${t('live.nextStop')} ${p.nextWaypoint.seq ?? ''}`}:</span>
          <b className="truncate">{p.nextWaypoint.label ?? '—'}</b>
          {p.nextWaypoint.plannedAt ? <span className="ms-auto flex flex-none items-center gap-1 text-text-muted"><Clock size={11} /> {fmtWhen(p.nextWaypoint.plannedAt, lang)}</span> : null}
        </div>
      ) : null}
      {p.routeSource === 'straight' && p.remainingKm !== null ? <div className="text-[10px] text-text-faint">{t('live.straightEstimate')}</div> : null}
    </div>
  );
}
